import React, { useState, useEffect, useCallback } from 'react';
import { approvalApi, projectApi } from '../services/api';
import { Card, Button, Select, Badge, Spinner, Input } from '../components/UI';
import { CheckCircle, XCircle, Clock, FileText } from '@phosphor-icons/react';

const STATUS_MAP = { pending: 'warning', approved: 'success', rejected: 'danger' };

export default function ApprovalsPage() {
  const [approvals, setApprovals] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('');
  const [filterProject, setFilterProject] = useState('');

  useEffect(() => { projectApi.list().then(r => setProjects(r.data)).finally(() => setLoading(false)); }, []);

  const loadApprovals = useCallback(async () => {
    const params = {};
    if (filterStatus) params.status = filterStatus;
    if (filterProject) params.project_id = filterProject;
    const res = await approvalApi.list(params);
    setApprovals(res.data);
  }, [filterStatus, filterProject]);

  useEffect(() => { loadApprovals(); }, [loadApprovals]);

  const handleReview = async (id, action, reason) => {
    try {
      await approvalApi.review(id, { action, reason: reason || undefined });
      loadApprovals();
    } catch (err) { alert(err.response?.data?.detail || 'Failed'); }
  };

  if (loading) return <div className="flex justify-center py-8"><Spinner size="lg" /></div>;

  const pending = approvals.filter(a => a.status === 'pending').length;

  return (
    <div className="space-y-4" data-testid="approvals-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-[#0A0A0A]">Approvals</h1>
          <p className="text-sm text-[#666]">Review and approve documents, variations & invoices</p>
        </div>
        {pending > 0 && (
          <div className="bg-[#FFAB00]/10 text-[#FFAB00] px-4 py-2 rounded-lg text-sm font-bold" data-testid="pending-count">
            {pending} Pending
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Select value={filterProject} onChange={(e) => setFilterProject(e.target.value)} data-testid="filter-project">
          <option value="">All Projects</option>
          {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
        </Select>
        <Select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} data-testid="filter-status">
          <option value="">All Statuses</option>
          <option value="pending">Pending</option>
          <option value="approved">Approved</option>
          <option value="rejected">Rejected</option>
        </Select>
      </div>

      {approvals.length === 0 ? (
        <Card className="p-8 text-center text-[#666]">No approval requests</Card>
      ) : (
        <div className="space-y-2">
          {approvals.map(a => (
            <Card key={a.id} className="p-3 sm:p-4" data-testid={`approval-row-${a.id}`}>
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                <div className="flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <FileText className="w-4 h-4 text-[#002FA7]" />
                    <span className="font-semibold text-sm">{a.title || a.entity_id}</span>
                    <Badge variant={STATUS_MAP[a.status]}>{a.status}</Badge>
                    <span className="text-[10px] bg-[#E5E5E5] px-1.5 py-0.5 rounded uppercase">{a.entity_type}</span>
                  </div>
                  <div className="flex flex-wrap gap-3 mt-1 text-xs text-[#666]">
                    <span>{a.project_name}</span>
                    <span>By: {a.submitted_by_name}</span>
                    {a.submitted_at && <span>{new Date(a.submitted_at).toLocaleDateString()}</span>}
                    {a.reviewed_by_name && <span>Reviewed: {a.reviewed_by_name}</span>}
                    {a.rejection_reason && <span className="text-[#FF3333] italic">Reason: {a.rejection_reason}</span>}
                  </div>
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  {a.status === 'pending' && (
                    <>
                      <Button size="sm" onClick={() => handleReview(a.id, 'approve')} data-testid={`approve-${a.id}`}>
                        <CheckCircle className="w-3 h-3 mr-1" /> Approve
                      </Button>
                      <Button size="sm" variant="danger" onClick={() => {
                        const r = prompt('Rejection reason:');
                        if (r) handleReview(a.id, 'reject', r);
                      }} data-testid={`reject-${a.id}`}>
                        <XCircle className="w-3 h-3 mr-1" /> Reject
                      </Button>
                    </>
                  )}
                  {a.status === 'approved' && <CheckCircle className="w-5 h-5 text-[#00C853]" weight="fill" />}
                  {a.status === 'rejected' && <XCircle className="w-5 h-5 text-[#FF3333]" weight="fill" />}
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
